<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useImpactOfDisabilityStore } from '@/impact-of-disability/impact-of-disability.store'
import SharedContentBanner from '@/components/SharedContentBanner.vue'
import ShareLinkInvalidMessage from '@/components/ShareLinkInvalidMessage.vue'
import TermsOfUseFooter from '@/components/TermsOfUseFooter.vue'
import ProgressSpinner from 'primevue/progressspinner'
import WikiTextEditor from '@/components/editor/WikiTextEditor.vue'
import type { LibraryImpactOfDisability } from '@/library/library_types'
import { transformResourceComponentsToWiki, transformWikiToResourceComponents } from '@/components/editor/wiki_tiptap_helpers'
import { getSharedImpactOfDisabilityStatement } from './api'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { IMPACT_OF_DISABILITY_EVENT_NAMES } from './event_names'

const analytics = getAnalyticsTracker()
const route = useRoute()
const impactOfDisabilityStore = useImpactOfDisabilityStore()

const toEmbeddedText = transformWikiToResourceComponents
const toWikiText = transformResourceComponentsToWiki

const statementUuid = computed(() => route.params.uuid as string)
const statement = ref<LibraryImpactOfDisability | null>(null)
const ownerName = ref<string>('')
const loading = ref(true)
const error = ref(false)

const fetchStatement = async () => {
  try {
    loading.value = true
    if (statementUuid.value) {
      const data = await getSharedImpactOfDisabilityStatement(statementUuid.value)
      statement.value = data
      ownerName.value = data.user_name || ''
    }
    analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.VIEWED_SHARED_STATEMENT, {
      iod_page: 'Shared Impact Statement',
      iod_selected_disability: statement.value?.disability,
      iod_selected_subjects: statement.value?.subjects,
      iod_selected_impact_areas: statement.value?.impact_areas?.map(ia => ia.area_label) || [],
    })
  } catch (err) {
    console.error('Error fetching shared IOD statement:', err)
    error.value = true
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await fetchStatement()
})
</script>

<template>
  <div>
    <SharedContentBanner
      v-if="!loading && !error && statement"
      content-type="an impact of disability statement"
      :owner-name="ownerName"
      :last-updated="statement.updated_at"
    />

    <div v-if="loading" class="flex justify-center items-center min-h-[400px]">
      <ProgressSpinner />
    </div>

    <div class="container mt-6" v-else-if="error">
      <ShareLinkInvalidMessage />
    </div>

    <div v-else-if="statement" class="container">
      <div class="flex justify-center mt-8 mb-6">
        <h1 class="text-display-xl font-medium text-gray-900 mt-8">
          Impact of Disability Statement
        </h1>
      </div>

      <div class="flex flex-col items-center max-w-3xl mx-auto">
        <h2 class="text-text-xl font-medium text-gray-900 self-start mb-2 pl-8">
          {{ statement.disability }}
        </h2>

        <WikiTextEditor
          v-model:wikiText="statement.statement"
          :toEmbeddedText="toEmbeddedText"
          :toWikiText="toWikiText"
          :reset-value="statement.statement"
          :outer-class="'rounded border border-gray-400 px-6 pb-6 w-full white-mode'"
          :read-only="true"
          editor-wrapper-class="pt-3"
        />
      </div>
      <TermsOfUseFooter />
    </div>
  </div>
</template>
